import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary capturó un error:', error, info);
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div
        role="alert"
        className="min-h-screen glass-deep flex items-center justify-center p-6"
      >
        <div className="max-w-md w-full glass-surface rounded-2xl p-8 shadow-xl border border-red-500/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-500/10 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-400" aria-hidden="true" />
            </div>
            <h2 className="text-xl font-semibold text-white">Algo salió mal</h2>
          </div>
          <p className="text-muted text-sm mb-6">
            Ha ocurrido un error inesperado. Puedes intentar recargar la sección o volver al inicio.
          </p>
          {import.meta.env.DEV && (
            <pre className="text-xs text-red-300 bg-black/30 rounded p-3 mb-4 overflow-auto max-h-40">
              {error.message}
            </pre>
          )}
          <button
            onClick={this.reset}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg font-semibold rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-cubus-lime focus:ring-offset-2 focus:ring-offset-cubus-bg"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            Reintentar
          </button>
        </div>
      </div>
    );
  }
}
